import { hideCookiePopup } from '@services/cookie-popup';
import { googleOneTapCheckUser, googleOneTapSignIn } from '@services/xhr-service';
import { loadWelcomeOrConfirmationReminderModal } from '@components/modal/welcomeOrConfirmationReminder/loader';
import { loadGoogleOneTapJppSecondStepModal } from '@components/modal/googleOneTap/jppSecondStep/loader';
import { getDomain } from '@services/email-service';
import { getWhat, getWhere, getRadius } from '@services/search-service';
import { JtagService } from './jtag-service';

export default class GoogleOneTapService {
  private static jrConfigGlobal: JrConfigGlobal = window.jrConfigGlobal;

  public static initialize = (config: IGoogleAccountsIdInitialize): void => {
    window.google.accounts.id.initialize(config);
  }

  public static oneTapPrompt = (): void => {
    window.google.accounts.id.prompt(
      (notification: IGoogleAccountsIdPrompt): void => {
        if (notification.isDisplayed()) {
          JtagService.trackSubscriptionProposal('google_one_tap');
        }
        if (notification.isNotDisplayed()) {
          GoogleOneTapService.trackError(notification.getNotDisplayedReason());
        }
        if (notification.isSkippedMoment()) {
          GoogleOneTapService.trackError(notification.getSkippedReason());
        }
        if (notification.isDismissedMoment()) {
          GoogleOneTapService.trackError(notification.getDismissedReason());
        }
      }
    );
  }

  public static handleResponse = async ({ credential }: IGoogleInitializeCallbackResponse): Promise<void> => {
    const { page: { isJobPreviewPage = false, isSearch = false } } = GoogleOneTapService.jrConfigGlobal;

    hideCookiePopup();

    if (credential) {
      if (isJobPreviewPage) {
        await GoogleOneTapService.jppCallback(credential);
      }
      if (isSearch) {
        await GoogleOneTapService.serpCallback(credential);
      }
    }
  }

  private static serpCallback = async (idToken: string): Promise<void> => {
    try {
      const signInResponse = await GoogleOneTapService.signIn(idToken, 'SERP')
      GoogleOneTapService.handleSignIn(signInResponse)
    } catch (error) {
      console.error(`serpCallback: ${error}`)
    }
  }

  private static jppCallback = async (idToken: string): Promise<void> => {
    try {
      const { ok: isResponseSuccessful = false, status } = await GoogleOneTapService.checkUser(idToken)
      if (status === 400) {
        return;
      }

      if (isResponseSuccessful) {
        const signInResponse = await GoogleOneTapService.signIn(idToken, 'JPP')
        GoogleOneTapService.handleSignIn(signInResponse)
      } else {
        loadGoogleOneTapJppSecondStepModal(idToken);
      }
    } catch (error) {
      console.error(`jppCallback: ${error}`)
    }
  }

  private static checkUser = (idToken: string): Promise<Response> => {
    const { googleOneTap: { timeout } } = GoogleOneTapService.jrConfigGlobal;

    return googleOneTapCheckUser({ idToken, timeout })
  }

  private static signIn = async (idToken: string, page: string): Promise<Response> => {
    const { googleOneTap: { timeout }, jobPreviewPage: { advertId = '' } = {} } = GoogleOneTapService.jrConfigGlobal;

    const signInPayload: IGoogleOneTapSignIn = {
      idToken,
      page,
      statement: {
        what: getWhat(),
        where: getWhere(),
        radius: getRadius(),
      },
      advertId,
      timeout,
    }

    return googleOneTapSignIn(signInPayload)
  }

  private static handleSignIn = async (response: Response): Promise<void> => {
    try {
      const { confirmed, responseUrl, email } = await response.json();

      if (confirmed) {
        GoogleOneTapService.confirmedJobseekerSuccessSignIn(responseUrl)
      } else {
        GoogleOneTapService.unconfirmedJobseekerSuccessSignIn(email)
      }
    } catch (error) {
      console.error(`handleSignIn: ${error}`)
    }
  }

  private static confirmedJobseekerSuccessSignIn = (responseUrl: string): void => {
    JtagService.trackCustomEvent('google_one_tap_login');

    if (responseUrl) {
      window.location.href = responseUrl;
    }
  }

  private static unconfirmedJobseekerSuccessSignIn = (email: string): void => {
    loadWelcomeOrConfirmationReminderModal({
      emailAddress: email,
      source: 'google_one_tap'
    });

    JtagService.trackSubscription('google_one_tap', getDomain(email) || '');

    GoogleOneTapService.hideNotificationPopup();
  }

  private static trackError = (reason: string = ''): void => {
    JtagService.trackCustomEvent('subscription_proposal_error', {
      source: 'google_one_tap',
      error: reason,
    });
  }

  private static hideNotificationPopup() {
    if (GoogleOneTapService.jrConfigGlobal.feature.notificationAlertBoxEnabled) {
      const notificationBoxes = document.querySelectorAll('.js-notification-alert');

      notificationBoxes.forEach((notificationBox) => notificationBox.classList.add('hide'));
    }
  }
}